import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { inject, Inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorService } from '../../components/core/services/error.service';
import { MaknaAuthenticationService } from '../../features/makna-authentication/services/makna-authentication.service';
import { AuthenticationStorageService } from '../../components/core/services/authentication-storage.service';
import { RequestHeaderHandlerService } from '../../components/core/services/request-header-handler.service';
import { BackToOriginService } from '../../shared/services/back-to-origin.service';
import { EXPIRED_NOTICE_ROUTE, EXPIRED_SESSION_NOTICE_ROUTE, GENERAL_ERROR_ROUTE, LOGIN_ROUTE } from '../constants/app-routes';
import { ErrorCodes } from '../enums/error-codes';
import { WEALTH_TOKEN } from '../../components/utils/variables';
import { AuthService } from '../../components/core/services/auth.service';
import { HttpStatusCodeEnum } from '@client-monorepo/common/network';
import { WEALTH_INTERCEPTOR_TOKEN_NOTIFIER } from '../../components/core/interceptors/interceptor-share.token';
import { NavigationConfig, WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { GeneralErrorModel } from '../models/general-error.model';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@client-monorepo/common/utilities';
import { environment } from '../environments/environment';
import { exceptionalErrorCodes } from '../constants/exceptional-error-codes';
import { AuthResponse } from '../../components/core/models/auth-response.interface';

const REFRESH_TOKEN_URL = 'users/token/refresh';
const DGP_REFRESH = 'login/dgp';
const MAX_RETRY_REFRESHING = 3;

@Injectable({
  providedIn: 'root',
})
export class HttpRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private retryCounter = 0;
  private messageService = inject(MessageService);
  private authService = inject(AuthService);
  private navigationService = inject(WealthNavigationService);
  private errorService = inject(ErrorService);
  private maknaAuthenticationService = inject(MaknaAuthenticationService);
  private authenticationStorageService = inject(AuthenticationStorageService);
  private requestHeaderHandlerService = inject(RequestHeaderHandlerService);
  private backToOrigin = inject(BackToOriginService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  constructor(
    @Inject(WEALTH_INTERCEPTOR_TOKEN_NOTIFIER)
    public tokenNotifier: BehaviorSubject<string | null>,
    @Inject('APP_CONFIG') private config: NavigationConfig,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any> | any> {
    if (!this.isWealthAppContext()) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (this.isUnauthorizedError(error)) {
          return this.handleUnauthorizedError(request, next, error);
        }

        if (this.isUnableToProceed(error)) {
          return this.handleUnProceedRequest(error, request);
        }

        if (this.isForbiddenRefreshError(request, error)) {
          this.backToOrigin.goBackToOrigin();
          return throwError(() => error);
        }

        this.handleOtherErrors(error, request);
        return throwError(() => error);
      }),
    );
  }

  private isWealthAppContext(): boolean {
    return this.config.appName === 'wealth' || this.router.url.startsWith('/mini-app/wealth');
  }

  private handleRefreshTokenRequestError(request: HttpRequest<any>, next: HttpHandler, error?: any): Observable<HttpEvent<any>> {
    this.retryCounter++;
    if (this.retryCounter < MAX_RETRY_REFRESHING) {
      return this.handle401Error(request, next);
    }

    if (this.isAuthenticationChallenged(error)) {
      this.navigationService.navigate([LOGIN_ROUTE]);
      return of();
    }

    this.authService.purgeAuth(true, true, '4');
    return of();
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, error?: any): Observable<HttpEvent<any>> {
    if (this.shouldRefreshToken(request)) {
      this.startTokenRefresh();

      if (this.isAuthenticationChallenged(error)) {
        return this.refreshWealthToken(request, next);
      }

      return this.refreshDGPToken(request, next);
    }

    return this.holdingRequest(request, next);
  }

  private holdingRequest(request: HttpRequest<any>, next: HttpHandler): any {
    return this.tokenNotifier.pipe(
      filter((token: string | null) => token !== null && token !== undefined),
      take(1),
      switchMap((token: string | null) => {
        return this.handleCachedRequest(request, next, token);
      }),
    );
  }

  private resetRetryCounter(): void {
    this.retryCounter = 0;
  }

  private handleCachedRequest(request: HttpRequest<any>, next: HttpHandler, response: any): Observable<HttpEvent<any>> {
    return !request.url.endsWith(REFRESH_TOKEN_URL)
      ? next.handle(this.requestHeaderHandlerService.updateHeader(request))
      : throwError(() => response);
  }

  private isUnableToProceed(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === HttpStatusCodeEnum.Unable_Process;
  }

  private handleUnProceedRequest(e: any, req: HttpRequest<any>): Observable<HttpEvent<any>> | null {
    const errorCode = e.error?.error?.code;
    if (exceptionalErrorCodes.includes(errorCode)) {
      return null;
    }
    switch (errorCode) {
      case ErrorCodes.SessionIsExpired:
        return this.handleSessionExpired();

      case ErrorCodes.passwordExpired:
        if (!req.url.endsWith('/v1/lead/collect')) {
          return this.handlePasswordExpired();
        }
        break;

      case ErrorCodes.invalidRefreshToken:
        this.handleInvalidRefreshToken();
        break;

      default:
        if (e.status === 422 && req.headers.get('x-clientpage')) {
          this.handleClientPageError(e, req);
        }
        break;
    }
    return null;
  }

  private generateUnplannedError(e: any, req: HttpRequest<any>) {
    if (this.shouldNavigateToGeneralError(req)) {
      const errorState: GeneralErrorModel = {
        title: this.translate.instant('PROVIDER_NOT_AVALIBLE_TITLE'),
        description: this.translate.instant('PROVIDER_NOT_AVALIBLE_DESCRIPTION'),
        pageTitle: this.generatePageTitle(req.headers.get('x-clientpage')),
        isState: true,
        icon: 'disconnected.svg',
      };
      this.navigationService.navigate([GENERAL_ERROR_ROUTE], {
        state: errorState,
      });
      return;
    }
    this.snackbarError(e);
  }

  private shouldNavigateToGeneralError(req: HttpRequest<any>): boolean {
    return !!req.headers.get('x-clientpage') && !req.url.includes(REFRESH_TOKEN_URL);
  }

  private snackbarError(error: any) {
    if (this.displaySnackbar(error?.error?.error?.code)) {
      const msg = error?.error?.error?.title || error?.error?.error?.description || `خطای فنی!`;
      const desc = !error?.error?.error?.title && !error?.error?.error?.description ? 'لطفاً دقایقی دیگر تلاش کنید.' : '';
      if (!error.url.includes('/api/files/')) {
        this.messageService.showErrorMessage(msg, desc);
      }
    }
  }

  private displaySnackbar(code: number): boolean {
    const suppressedCodes = new Set<number | undefined>([
      ErrorCodes.kYCShahkarCellNumberMissmatchNationalId,
      ErrorCodes.CustomerNotRegisteredInFund,
      ErrorCodes.customerNotExist,
      ErrorCodes.CustomerIsNotSejami,
      ErrorCodes.SessionIsExpired,
      ErrorCodes.validateNationalIdFailed,
      ErrorCodes.InvalidOtp,
      undefined,
    ]);

    return !suppressedCodes.has(code);
  }

  private refreshWealthToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.maknaAuthenticationService.refresh().pipe(
      switchMap((response) => {
        this.handleSuccessfulRefresh(response);
        return this.handleCachedRequest(request, next, response);
      }),
      catchError((error) => this.handleRefreshError(error, request, next)),
      finalize(() => this.onTokenRefreshComplete()),
    );
  }

  private handleSuccessfulRefresh(response: any): void {
    this.resetRetryCounter();
    this.tokenNotifier.next(response?.result?.accessToken || null);
  }

  private handleRefreshError(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return request.url.endsWith(REFRESH_TOKEN_URL) || request.url.endsWith(DGP_REFRESH)
      ? next.handle(error)
      : this.holdingRequest(request, next);
  }

  private refreshDGPToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authService.postRefreshToken().pipe(
      switchMap((response) => this.handleDGPTokenRefreshSuccess(response, request, next)),
      catchError((err) => this.handleRefreshError(err, request, next)),
      finalize(() => this.onTokenRefreshComplete()),
    );
  }

  private handleDGPTokenRefreshSuccess(response: AuthResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.resetRetryCounter();
    this.authenticationStorageService.updateAuth(response);
    const wealthTokenExists = !!JSON.parse(localStorage.getItem(WEALTH_TOKEN) || 'false');
    if (wealthTokenExists) {
      return this.refreshWealthToken(request, next);
    }
    return this.handleCachedRequest(request, next, response);
  }

  private onTokenRefreshComplete(): void {
    this.isRefreshing = false;
  }

  private providerNotAvalible(errorCode: number): boolean {
    const providerNotAvalibleErrors = [ErrorCodes.timeout, ErrorCodes.BitpendarRequestTimeout];
    return providerNotAvalibleErrors.includes(errorCode);
  }

  private generatePageTitle(xClient: string | null): string {
    if (xClient?.includes('purchase')) return 'BUY';
    return 'ERROR';
  }

  private handleUnauthorizedError(request: HttpRequest<any>, next: HttpHandler, error: any): Observable<HttpEvent<any>> {
    if (request.url.endsWith(REFRESH_TOKEN_URL)) {
      return this.handleRefreshTokenRequestError(request, next, error);
    }

    if (request.url.includes('/digipay/api/users/login')) {
      return throwError(() => error);
    }

    return this.handle401Error(request, next, error);
  }

  private isForbiddenRefreshError(request: HttpRequest<any>, error: any): boolean {
    return request.url.endsWith(REFRESH_TOKEN_URL) && error.status === HttpStatusCodeEnum.Forbidden;
  }

  private handleOtherErrors(error: any, request: HttpRequest<any>): void {
    if (
      error.status !== HttpStatusCodeEnum.ServerError &&
      !error.url?.includes('api/files') &&
      !error.url?.includes('/v1/collateral/launch')
    ) {
      this.generateUnplannedError(error, request);
    } else {
      this.snackbarError(error);
    }
  }

  private isUnauthorizedError(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === HttpStatusCodeEnum.Unauthorized;
  }

  private isAuthenticationChallenged(error: any): boolean {
    return error?.error?.error?.title === 'AuthenticationChallenged';
  }

  private shouldRefreshToken(request: HttpRequest<any>): boolean {
    return !this.isRefreshing || (request.url.endsWith(REFRESH_TOKEN_URL) && this.retryCounter < MAX_RETRY_REFRESHING);
  }

  private startTokenRefresh(): void {
    this.isRefreshing = true;
    this.tokenNotifier.next(null);
  }

  private handleSessionExpired(): null {
    localStorage.removeItem(WEALTH_TOKEN);
    this.navigationService.navigate([EXPIRED_SESSION_NOTICE_ROUTE]);
    return null;
  }

  private handlePasswordExpired(): null {
    this.navigationService.navigate([EXPIRED_NOTICE_ROUTE]);
    return null;
  }

  private handleInvalidRefreshToken(): void {
    localStorage.removeItem(WEALTH_TOKEN);
    this.maknaAuthenticationService.loginDGP().subscribe({
      next: (res) => {
        if (res?.success) {
          if (res.result.requiresPassword) {
            const redirectUrl =
              this.navigationService.getCurrentUrl() !== '/' ? this.navigationService.getCurrentUrl() : environment.afterLoginUrl;
            localStorage.setItem('redirectUrl', redirectUrl);
            const queryParams = this.activatedRoute.snapshot.queryParams;
            this.navigationService.navigate([LOGIN_ROUTE], { queryParams });
          } else {
            window.location.reload();
          }
        } else {
          this.handleRedirectToDPX();
        }
      },
    });
  }

  private handleRedirectToDPX() {
    sessionStorage.setItem('redirectUrlAfterLogin', window.location.href);
    window.open(environment.supperAppLoginUrl, '_self');
  }

  private handleClientPageError(e: any, req: HttpRequest<any>): void {
    const errorCode = e?.error?.error?.code;
    if (errorCode === ErrorCodes.rayanGetCustomerOrdersFailed || errorCode === ErrorCodes.rayanCancelOrderOtpFailed) {
      e.error.error['isState'] = true;
      e.error.error['pageTitle'] = req.headers.get('x-clientpage');
      this.errorService.setGeneralError(e.error.error);
      this.navigationService.navigate([GENERAL_ERROR_ROUTE]);
    } else if (this.providerNotAvalible(errorCode)) {
      this.generateUnplannedError(e, req);
    } else {
      this.snackbarError(e);
    }
  }
}
